import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { SpinnerService } from '../services';
import { environment } from '@cpd-app/environment';

/**
 * PL-3. The page-wide spinner used to hide only on an ERROR.
 *
 * ---------------------------------------------------------------------------
 * WHAT WAS WRONG
 *
 * The old implementation kept an array of in-flight URLs. On success it
 * filtered the finished URL out of that array -- and then did nothing. There
 * was no `hideSpinner()` on the success path at all. The only call to it lived
 * inside `catchError`.
 *
 * So a REST call that SUCCEEDED left the spinner running over the page
 * indefinitely. Nothing is live today only because every current caller sets
 * `X-Silent-Request` as a workaround, several of them with comments citing
 * this exact bug -- which means the defect was understood, routed around
 * caller by caller, and left in place. The next service that forgets the
 * header reproduces it.
 *
 * Three smaller faults in the same twenty lines, all fixed by the rewrite:
 *
 *  - It matched responses to requests by URL STRING. Two concurrent calls to
 *    the same endpoint shared one entry, so the first response cleared both.
 *  - It read `res.url`, which is the FINAL url after redirects and is null on
 *    some responses, so a redirected request could never be matched off the
 *    list at all.
 *  - It handled completion and error but not CANCELLATION. A `switchMap` that
 *    drops an in-flight request -- which is how every typeahead in this app
 *    works -- left its entry on the list permanently.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT DOES NOW
 *
 * A counter, and `finalize`. `finalize` runs on complete, on error AND on
 * unsubscribe, which is the whole of what "this request is no longer in
 * flight" means -- and it is why the counter cannot drift.
 *
 * `X-Silent-Request` still works and still means "do not show the spinner for
 * this one". Callers who set it purely as a workaround for the bug can drop
 * it, but nothing breaks if they do not.
 */
@Injectable()
export class SpinnerInterceptor implements HttpInterceptor {
  /**
   * How many tracked requests are in flight.
   *
   * A count, not a list of URLs: the question this needs to answer is "is
   * anything still running", and a list keyed on a value that is neither
   * unique nor stable cannot answer it.
   */
  private inFlight = 0;

  constructor(private spinnerService: SpinnerService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isSilent = req.headers.has('X-Silent-Request');
    const isApiRequest = req.url.indexOf(environment.apiUrlBase) >= 0;
    const isBgRequest = req.url.indexOf('burning-glass') >= 0;
    const tracked = !isSilent && (isApiRequest || isBgRequest);

    if (!tracked) {
      // Untracked requests must not touch the count. The old code still ran
      // its "is this the first request" check for them, so a silent request
      // could show the spinner on behalf of something else.
      return next.handle(req);
    }

    if (this.inFlight === 0) {
      this.spinnerService.showSpinner();
    }
    this.inFlight++;

    return next.handle(req).pipe(
      finalize(() => {
        // Floored at zero. The count should never go negative, and if some
        // future change makes it possible, an early hide is a far better
        // failure than a spinner stuck over the page -- which is the bug this
        // replaces.
        this.inFlight = Math.max(0, this.inFlight - 1);
        if (this.inFlight === 0) {
          this.spinnerService.hideSpinner();
        }
      })
    );
  }
}
